import { stripMarkdown } from 'lib/string/stripMarkdown'

// Mirrors svc-trip assistant-surface protocol string limits. Shared by every surface adapter
// (offer page, trip planner, ...) that composes a plain-text label for the protocol -- extracted
// here rather than left offer-page-local so a second surface needing the same sanitization/length
// rules imports this instead of re-deriving it a second time.
export const ASSISTANT_SURFACE_LABEL_MAX_LENGTH = 80

const MARKDOWN_LINK = /!?\[([^\]]+)\]\([^)]*\)/g
const HTML_TAG = /<[^>]*>/g
const URL_SCHEME = /(?:^|[\s([])[A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s)\]]+/g
const DISALLOWED_TEXT_PROTOCOL = /(?:^|[\s([])(?:surface-target|hotel|experience|cruise|place|place-search|http|https|ftp|javascript|data|vbscript|file|mailto):[^\s)\]]+/gi

function replaceControlCharacters(value: string, preserveLineBreaks: boolean): string {
  return [...value].map(character => {
    if (preserveLineBreaks && (character === '\n' || character === '\r')) {
      return character
    }

    const codePoint = character.codePointAt(0)
    return codePoint !== undefined && (codePoint < 0x20 || codePoint === 0x7F) ? ' ' : character
  }).join('')
}

function stripUrlsAndProtocols(value: string): string {
  return value
    .replace(URL_SCHEME, ' ')
    .replace(DISALLOWED_TEXT_PROTOCOL, ' ')
}

interface SanitizeAssistantSurfacePlainTextOptions {
  /**
   * The snapshot path needs every whitespace run (including newlines) collapsed to a single
   * space for contentHash stability. The legacy per-message surfaceContext path has no such
   * constraint, and its bodies are built by joining structured parts with \n/\n\n (e.g. bedbank
   * check-in times, fees, policies) -- collapsing those the same way fuses distinct clauses into
   * one run-on line. Set this for that path to collapse only horizontal whitespace and keep \n
   * runs intact.
   */
  preserveLineBreaks?: boolean
}

export function sanitizeAssistantSurfacePlainText(
  value: string | undefined | null,
  { preserveLineBreaks = false }: SanitizeAssistantSurfacePlainTextOptions = {},
): string | undefined {
  if (!value) {
    return undefined
  }

  const withoutControlCharacters = replaceControlCharacters(
    stripUrlsAndProtocols(
      stripMarkdown(
        value
          .replace(MARKDOWN_LINK, '$1')
          .replace(HTML_TAG, ' '),
      ),
    ),
    preserveLineBreaks,
  )

  const collapsed = preserveLineBreaks
    ? withoutControlCharacters.replace(/\r\n|\r/g, '\n').replace(/[ \t]+/g, ' ').trim()
    : withoutControlCharacters.replace(/\s+/g, ' ').trim()

  return collapsed || undefined
}

export function boundedAssistantSurfacePlainText(value: string | undefined | null, maxLength: number): string | undefined {
  if (!value) {
    return undefined
  }

  const sanitized = sanitizeAssistantSurfacePlainText(value)
  if (!sanitized) {
    return undefined
  }

  // svc-trip trims every bounded string server-side before hashing/validating it, so a slice that
  // lands on a space must be trimmed here too -- otherwise the client's contentHash is computed
  // over a trailing-space string the server never sees, and the snapshot sync 400s.
  const truncated = sanitized.length > maxLength ? sanitized.slice(0, maxLength).trim() : sanitized
  return truncated || undefined
}

export function toAssistantSurfaceLabel(value: string | undefined | null): string | undefined {
  return boundedAssistantSurfacePlainText(value, ASSISTANT_SURFACE_LABEL_MAX_LENGTH)
}

// Normalizes to a safe id charset without truncating -- unlike toAssistantSurfaceLabel, callers
// composing an id into a larger string (e.g. a lookup pointer a model might read back) need the
// id itself left intact; truncating it would silently corrupt the id rather than just shortening
// display text.
export function toAssistantSurfaceId(value: string | undefined): string | undefined {
  const normalized = value
    ?.trim()
    .replace(/[^A-Za-z0-9._:-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  return normalized || undefined
}
